import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/common/DashboardBits";
import ExecFormDialog from "@/components/common/ExecFormDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, KeyRound, Loader2, Users, UserX } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";

const UNASSIGNED = "__unassigned__";
const ALL = "__all__";

export default function ExecutivesPage() {
  const { t } = useLang();
  const [list, setList] = useState([]);
  const [managers, setManagers] = useState([]);
  const [open, setOpen] = useState(false);
  const [openUser, setOpenUser] = useState(null);
  const [filterMgr, setFilterMgr] = useState(ALL);
  const [savingId, setSavingId] = useState(null);

  const load = () => {
    api.get("/executives").then((r) => setList(r.data));
    api.get("/managers").then((r) => setManagers(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const managerName = (id) => managers.find((m) => m.id === id)?.name || "—";

  const filtered = useMemo(() => {
    if (filterMgr === ALL) return list;
    if (filterMgr === UNASSIGNED) return list.filter((e) => !e.manager_id);
    return list.filter((e) => e.manager_id === filterMgr);
  }, [list, filterMgr]);

  const stats = useMemo(() => {
    const unassigned = list.filter((e) => !e.manager_id).length;
    return { total: list.length, unassigned, assigned: list.length - unassigned };
  }, [list]);

  const assignManager = async (execId, newMgrId) => {
    setSavingId(execId);
    try {
      const payload = { manager_id: newMgrId === UNASSIGNED ? null : newMgrId };
      await api.patch(`/executives/${execId}/manager`, payload);
      toast.success(newMgrId === UNASSIGNED ? "Unassigned from manager" : "Manager assigned");
      // optimistic local update
      setList((prev) => prev.map((e) => e.id === execId ? { ...e, manager_id: payload.manager_id, is_approved: !!payload.manager_id } : e));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div>
      <PageHeader
        title={t("executives")}
        subtitle={`${stats.total} total · ${stats.assigned} assigned · ${stats.unassigned} unassigned`}
        right={
          <div className="flex flex-wrap items-center gap-2">
            <Select value={filterMgr} onValueChange={setFilterMgr}>
              <SelectTrigger className="w-[220px]" data-testid="exec-filter-manager">
                <SelectValue placeholder="Filter by manager" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL} data-testid="exec-filter-all">All managers</SelectItem>
                <SelectItem value={UNASSIGNED} data-testid="exec-filter-unassigned">
                  <span className="inline-flex items-center gap-2"><UserX className="h-3.5 w-3.5" /> Unassigned</span>
                </SelectItem>
                {managers.map((m) => (
                  <SelectItem key={m.id} value={m.id} data-testid={`exec-filter-${m.id}`}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-slate-950 hover:bg-slate-800" data-testid="add-exec-button">
                  <Plus className="h-4 w-4 mr-1" /> {t("add_executive")}
                </Button>
              </DialogTrigger>
              <ExecFormDialog
                showManagerPicker
                showPaymentTerms
                onClose={() => setOpen(false)}
                onSaved={() => { setOpen(false); load(); }}
              />
            </Dialog>
          </div>
        }
      />

      <Card className="border border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Working Location</TableHead>
                <TableHead className="w-[220px]">Reporting Manager</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Rate</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">
                  {list.length === 0 ? "No executives yet." : "No executives match this filter."}
                </TableCell></TableRow>
              )}
              {filtered.map((e) => (
                <TableRow key={e.id} data-testid={`exec-row-${e.id}`}>
                  <TableCell className="font-medium">
                    {e.name}
                    <div className="text-[11px] text-slate-500 mt-0.5">Pincode {e.pincode || "—"} · {e.working_radius_km || 0} km radius</div>
                  </TableCell>
                  <TableCell className="text-slate-600 text-xs">{e.email}<br/>{e.phone}</TableCell>
                  <TableCell className="text-slate-600">{e.working_location}</TableCell>
                  <TableCell data-testid={`exec-manager-cell-${e.id}`}>
                    <div className="flex items-center gap-1.5">
                      <Users className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                      <Select
                        value={e.manager_id || UNASSIGNED}
                        onValueChange={(v) => assignManager(e.id, v)}
                        disabled={savingId === e.id}
                      >
                        <SelectTrigger className="h-8 text-sm border-slate-200" data-testid={`exec-manager-select-${e.id}`}>
                          <SelectValue placeholder="Assign manager" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={UNASSIGNED} data-testid={`exec-manager-option-none-${e.id}`}>
                            <span className="text-slate-500">— Unassigned —</span>
                          </SelectItem>
                          {managers.map((m) => (
                            <SelectItem key={m.id} value={m.id} data-testid={`exec-manager-option-${m.id}-${e.id}`}>
                              {m.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {savingId === e.id && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
                    </div>
                  </TableCell>
                  <TableCell>
                    {e.is_approved
                      ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-semibold">Active</span>
                      : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[11px] font-semibold">Pending</span>
                    }
                  </TableCell>
                  <TableCell className="num text-slate-600">₹ {e.rate_per_report}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => setOpenUser(e)} data-testid={`exec-create-user-${e.id}`}><KeyRound className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={async () => {
                      if (!window.confirm("Delete executive?")) return;
                      try { await api.delete(`/executives/${e.id}`); toast.success("Deleted"); load(); }
                      catch (err) { toast.error(formatApiError(err)); }
                    }} data-testid={`exec-delete-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {openUser && (<CreateUserDialog entity={openUser} role="field" onClose={() => setOpenUser(null)} />)}
    </div>
  );
}

function CreateUserDialog({ entity, role, onClose }) {
  const [form, setForm] = useState({ email: entity?.email || "", password: "", name: entity?.name || "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/auth/register", {
        email: form.email.toLowerCase(), password: form.password, name: form.name,
        role, entity_id: entity.id, phone: entity.phone,
      });
      toast.success("Login user created");
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Create Executive Login</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div><Label>Temporary Password</Label><Input required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-slate-950 hover:bg-slate-800">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
